import React, { useState } from 'react';
import { UploadCloud, FileSpreadsheet, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function FinalAwardsUpload() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem("admin_token") || "";
      const response = await fetch('/api/admin/final-awards/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setResult({ success: true, count: data.count });
        toast.success(`Successfully registered awards for ${data.count} students.`);
        setFile(null); // Reset after success
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (err) {
      console.error(err);
      setResult({ success: false, message: err.message });
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Final Awards Registry</h1>
        <p className="text-gray-500 mt-1">
          Upload an Excel sheet containing Final CGPA, Final Result, and Division Awarded. 
          This data is permanently saved and will automatically apply to all generated Final Semester Grade Sheets and Tabulation Charts.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        
        {/* Upload Zone */}
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:bg-gray-50 transition-colors relative">
          <input 
            type="file" 
            accept=".xlsx, .xls" 
            onChange={handleFileChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          <div className="flex flex-col items-center justify-center pointer-events-none">
            {file ? (
              <>
                <FileSpreadsheet className="w-12 h-12 text-blue-500 mb-3" />
                <p className="text-lg font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
              </>
            ) : (
              <>
                <UploadCloud className="w-12 h-12 text-gray-400 mb-3" />
                <p className="text-lg font-medium text-gray-900">Drop your Excel file here</p>
                <p className="text-sm text-gray-500 mt-1">Must contain columns: ROLL, CGPA, RESULT, DIVISION</p>
              </>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
            {loading ? 'Processing...' : 'Upload Registry'}
          </button>
        </div>

        {/* Result Message */}
        {result && (
          <div className={`mt-6 p-4 rounded-lg flex items-start gap-3 ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {result.success ? (
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
            )}
            <div>
              <h3 className={`font-medium ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                {result.success ? 'Upload Successful' : 'Upload Failed'}
              </h3>
              <p className={`text-sm mt-1 ${result.success ? 'text-green-600' : 'text-red-600'}`}>
                {result.success 
                  ? `The registry has been updated and securely saved. Extracted final awards for ${result.count} roll numbers.` 
                  : result.message}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 bg-blue-50 text-blue-800 p-4 rounded-lg text-sm border border-blue-100">
        <strong>Formatting Tip:</strong> Ensure your Excel file has columns with headers that contain the words <code>ROLL</code>, <code>CGPA</code>, <code>RESULT</code>, and <code>DIVISION</code>. The script checks the first 30 rows to dynamically find your headers.
      </div>
    </div>
  );
}
